// Pénzügyi chartok (a bevétel- és a kiadás-oldal közös készlete) — **Recharts** (v3) a design-rendszer tokenjeivel, világos+sötét témában.
//
// ── MIÉRT RECHARTS, ÉS MIÉRT ÍGY ────────────────────────────────────────────────────────────────────────
// A korábbi változat kézzel rajzolt SVG/CSS volt (polyline + CSS-sávok + stroke-dasharray donut). Működött, de
// nem tudott tengelyeket, rácsot, hover-tooltipet és animációt — a vezetői nézetnek pedig épp ezek adják a
// leolvashatóságot. A Recharts a React-ökoszisztéma bevett választása erre, deklaratív és SVG-alapú.
//
// ── SZÍNEK: TOKEN-VEZÉRELVE, JS NÉLKÜL ─────────────────────────────────────────────────────────────────
// A paletta CSS-változókban él (`--brand`, `--violet`, …, RGB-triplet alakban). A Recharts a színt
// ATTRIBÚTUMKÉNT írja ki (`fill="…"`), ahol a `var()` NEM érvényes — ezért NEM adunk át hex-et vagy
// `rgb(var(--x))` sztringet. Helyette:
//   • ahol a Recharts engedi, `className`-nel festünk (`fill-brand`) — a CSS-osztály FELÜLÍRJA a
//     prezentációs attribútumot (a CSS mindig erősebb), tehát a szín a tokenből jön,
//   • a gradiens-`<stop>`-oknál Tailwind arbitrary property (`[stop-color:rgb(var(--brand))]`) — ez is CSS.
// Így a téma-váltás (html.theme-dark/light) AZONNAL hat, JS-hook és újrarender nélkül; és nincs hardcode-olt
// hex a chartokban (a design-system ESLint-fal ezt egyébként tiltja is).
//
// ── HOZZÁFÉRHETŐSÉG ────────────────────────────────────────────────────────────────────────────────────
// Minden chart: a vizuál `aria-hidden`, MELLETTE `sr-only` lista a NEVEKKEL és a PONTOS összegekkel — a
// screen reader tehát a valós adatot olvassa, nem egy „diagram" címkét. A Recharts `accessibilityLayer`
// a billentyűzet-navigációt adja a kartéziánus chartokon.
//
// ── PÉNZ ───────────────────────────────────────────────────────────────────────────────────────────────
// Az összegek Decimal-STRINGEK. A `toNum` KIZÁRÓLAG geometriára/arányra megy; a MEGJELENÍTETT érték MINDIG a
// hívó `fmt`/`fmtShort` formázója a nyers stringen — így nincs float-kerekítés a felületen.
import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  Rectangle,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  type BarShapeProps,
} from 'recharts';

import { Text } from '@/components/ui/text';
import { formatPercent } from '@/lib/format-percent';

/** Decimal-string → szám (CSAK arány-/geometria-számításhoz; megjelenítésre a `fmt` a string-en dolgozik). */
export function toNum(value: string): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

// Főkategória → Tailwind token-osztályok. FONTOS: LITERÁLIS osztálynevek (a Tailwind JIT csak a forrásban
// szó szerint megjelenő osztályokat generálja — futásidőben összefűzött név nem kerülne a CSS-be).
// product=zöld brand · advisory=violet · other=semleges.
const CATEGORY_FILL: Record<string, string> = {
  product: 'fill-brand',
  advisory: 'fill-violet',
  other: 'fill-neutral-400',
};
const CATEGORY_SWATCH: Record<string, string> = {
  product: 'bg-brand',
  advisory: 'bg-violet',
  other: 'bg-neutral-400',
};

export type Formatter = (value: string) => string;

/** A rács/tengely vonalszíne — a táblázat-hajszálvonal tokenje (világos+sötét egyaránt). */
export const AXIS_LINE = 'var(--table-hairline)';
/** Tengely-feliratok: a `Text` másodlagos tónusa (11px, muted) — a Recharts `tick` stílusa. */
export const TICK_STYLE = { fontSize: 11, fill: 'currentColor' } as const;

/** A kategória-tengely felirat-szélessége (px) és a belőle következő karakter-plafon 11px-es szedésnél. */
const CATEGORY_AXIS_WIDTH = 168;
const CATEGORY_TICK_CHARS = 24;

/**
 * EGYSOROS kategória-tick. A Recharts alap-tickje a hosszú feliratot `tspan`-okra TÖRDELI, amitől a sorok
 * egymásba futnak (mérve: „Digital marketing együttműködés (Digital Marketing)" 3 sor lett, átlapolva a
 * szomszéd sávval). Az SVG `<text>` viszont nem tud CSS-ellipszist, ezért KARAKTER-szinten vágunk, és a
 * TELJES nevet a tooltip adja (illetve az `sr-only` lista, szó szerint).
 */
function CategoryTick({
  x,
  y,
  payload,
}: {
  x?: number;
  y?: number;
  /** A Recharts tick-payloadja: a kategória-tengelyen string, az érték-tengelyen szám. */
  payload?: { value?: string | number };
}): JSX.Element {
  const raw = typeof payload?.value === 'string' ? payload.value : String(payload?.value ?? '');
  const label =
    raw.length > CATEGORY_TICK_CHARS ? `${raw.slice(0, CATEGORY_TICK_CHARS - 1)}…` : raw;
  return (
    <text x={x} y={y} dy={4} textAnchor="end" fontSize={11} fill="currentColor">
      {label}
    </text>
  );
}

/**
 * EGYSOROS érték-tick az összeg-tengelyre. Ugyanaz a probléma, mint a kategória-ticknél: a Recharts alap-tickje
 * SZÓHATÁRON tördel, ezért a „80 M HUF" két sorba került (mérve) — a tengely így magasabb lett a sorosztásnál,
 * és a feliratok összefolytak. Egy `<text>`, tördelés nélkül.
 */
export function MoneyTick({
  x,
  y,
  payload,
  format,
}: {
  x?: number;
  y?: number;
  /** A Recharts tick-payloadja az érték-tengelyen szám (a formázó a nyers stringen dolgozik). */
  payload?: { value?: string | number };
  format: Formatter;
}): JSX.Element {
  return (
    <text x={x} y={y} dy={4} textAnchor="end" fontSize={11} fill="currentColor">
      {format(String(payload?.value ?? 0))}
    </text>
  );
}

/** Közös chart-kártya váz: cím + opcionális jobb-fejléc + üres-állapot. */
export function ChartCard({
  title,
  aside,
  isEmpty,
  emptyLabel,
  children,
}: {
  title: string;
  aside?: ReactNode;
  isEmpty?: boolean;
  emptyLabel: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <div className="card-glass text-card-foreground flex flex-col overflow-hidden rounded-xl">
      {/* A kártya FEJLÉCE alatt nincs vonal (user-döntés, 2026-08-26) — a hierarchiát a tipográfia és a
          térköz adja, mint a modál-fejlécnél. */}
      <div className="flex items-center justify-between gap-2 px-4 py-3">
        <Text variant="itemTitle" as="h3">
          {title}
        </Text>
        {aside}
      </div>
      <div className="flex-1 p-4">
        {isEmpty ? (
          <div className="flex h-full min-h-32 items-center justify-center">
            <Text variant="secondary" as="span">
              {emptyLabel}
            </Text>
          </div>
        ) : (
          children
        )}
      </div>
    </div>
  );
}

/**
 * Közös tooltip-kártya — a felület `card-glass` nyelvén (nem a Recharts alap-fehér doboza). Egy vagy több
 * sor: szín-pötty + címke + FORMÁZOTT összeg.
 */
export function TooltipCard({
  label,
  rows,
}: {
  label: string;
  rows: { key: string; label?: string; value: string; swatch?: string }[];
}): JSX.Element {
  return (
    // ÁTLÁTSZATLAN háttér (`bg-popover`): az üveg-háttéren átütött az alatta lévő felirat — a fánk közepén álló
    // összeg úgy látszott, mintha a tooltip szövegének része lenne (user-lelet)
    <div className="bg-popover text-popover-foreground rounded-lg border px-3 py-2 shadow-lg">
      <div className="text-foreground mb-1 text-xs font-medium">{label}</div>
      <ul className="space-y-0.5">
        {rows.map((r) => (
          <li key={r.key} className="flex items-center gap-2 text-xs">
            {r.swatch && <span className={`${r.swatch} size-2 shrink-0 rounded-full`} />}
            {r.label && <span className="text-muted-foreground">{r.label}</span>}
            <span className="text-foreground ml-auto tabular-nums">{r.value}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Rejtett, de FELOLVASOTT adat-lista (a vizuál `aria-hidden`) — a chart tartalma szövegesen is megvan. */
export function SrOnlyData({
  ariaLabel,
  items,
}: {
  ariaLabel: string;
  items: { key: string; label: string; value: string }[];
}): JSX.Element {
  return (
    <ul className="sr-only" aria-label={ariaLabel}>
      {items.map((i) => (
        <li key={i.key}>{`${i.label}: ${i.value}`}</li>
      ))}
    </ul>
  );
}

// ── 1. MRR-trend (12 hónap) — terület-chart gradienssel ─────────────────────────────────────────
export function TrendChart({
  data,
  fmt,
  fmtShort,
  monthLabel,
  ariaLabel,
}: {
  data: { month: string; amount: string }[];
  fmt: Formatter;
  /** Rövidített alak a tengely-feliratokhoz (pl. „6,5 M") — a hosszú összeg ott olvashatatlan. */
  fmtShort: Formatter;
  /** `YYYY-MM` → rövid tengelycímke (pl. hónap-szám). */
  monthLabel: (month: string) => string;
  ariaLabel: string;
}): JSX.Element {
  const rows = data.map((d) => ({
    month: d.month,
    label: monthLabel(d.month),
    amount: d.amount,
    v: toNum(d.amount),
  }));

  return (
    <div>
      <div className="text-muted-foreground h-44 w-full" aria-hidden="true">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={rows}
            margin={{ top: 8, right: 4, bottom: 0, left: 4 }}
            accessibilityLayer
          >
            <defs>
              {/* A gradiens a brand-tokenből (CSS!), nem hardcode-olt hexből — téma-váltásra is helyes. */}
              <linearGradient id="rf-mrr-area" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" className="[stop-color:rgb(var(--brand))] [stop-opacity:0.35]" />
                <stop offset="100%" className="[stop-color:rgb(var(--brand))] [stop-opacity:0]" />
              </linearGradient>
            </defs>
            {/* CSAK vízszintes rács: a függőleges vonalak 12 hónapnál rácsketrecet adnának. */}
            <CartesianGrid vertical={false} stroke={AXIS_LINE} strokeDasharray="3 3" />
            <XAxis
              dataKey="label"
              tick={TICK_STYLE}
              tickLine={false}
              axisLine={{ stroke: AXIS_LINE }}
              interval="preserveStartEnd"
              minTickGap={16}
            />
            <YAxis
              tick={<MoneyTick format={fmtShort} />}
              tickLine={false}
              axisLine={false}
              // A „80 M HUF" alak elférjen egy sorban (a devizát nem vágjuk le — pénz-tengelynél az egység kell).
              width={72}
            />
            <Tooltip
              cursor={{ stroke: AXIS_LINE }}
              content={({ active, payload }) => {
                const row = payload?.[0]?.payload as (typeof rows)[number] | undefined;
                if (!active || !row) return null;
                return (
                  <TooltipCard label={row.label} rows={[{ key: 'mrr', value: fmt(row.amount) }]} />
                );
              }}
            />
            <Area
              type="monotone"
              dataKey="v"
              className="stroke-brand"
              strokeWidth={2}
              fill="url(#rf-mrr-area)"
              // A pont CSAK hoverre — 12 pont állandóan kirakva zajos, a trend a vonal.
              dot={false}
              activeDot={{ r: 4, className: 'fill-brand stroke-background', strokeWidth: 2 }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      <SrOnlyData
        ariaLabel={ariaLabel}
        items={rows.map((r) => ({ key: r.month, label: r.label, value: fmt(r.amount) }))}
      />
    </div>
  );
}

// ── 1/b. TERV vs TÉNY (12 hónap) — két sorozat egy tengelyen ───────────────────────────────────
//
// MIÉRT KÜLÖN CHART az MRR-trend mellett: az MRR-trend CSAK a havi frekvenciájú tételeket méri, a tény-oldal
// viszont MINDENT, ami számlára került. A kettőt egy diagramra téve a „terv" rendszeresen a „tény" alatt
// futna — nem hibából, hanem mert más a két mennyiség. Ez a chart a `planTrend` (teljes havi eredmény) és az
// `actualTrend` (kiszámlázott) párost rajzolja, tehát a két vonal UGYANAZT méri.
//
// A TÉNY-VONAL SZÁNDÉKOSAN MEGSZAKAD a még nyitott hónapoknál (`amount: null`): ott nincs adat, és egy
// 0-ra húzott vonal azt állítaná, hogy abban a hónapban semmit sem számláztunk. A Recharts a `null`
// értéknél magától megszakítja a vonalat (`connectNulls` nélkül), a váltóvonal pedig ott látszik, ahol a
// tény-sorozat véget ér — külön jelmagyarázattal.
export function PlanVsActualChart({
  plan,
  actual,
  fmt,
  fmtShort,
  monthLabel,
  planLabel,
  actualLabel,
  ariaLabel,
}: {
  plan: { month: string; amount: string }[];
  actual: { month: string; amount: string | null }[];
  fmt: Formatter;
  fmtShort: Formatter;
  monthLabel: (month: string) => string;
  planLabel: string;
  actualLabel: string;
  ariaLabel: string;
}): JSX.Element {
  const actualByMonth = new Map(actual.map((a) => [a.month, a.amount]));
  const rows = plan.map((p) => {
    const raw = actualByMonth.get(p.month) ?? null;
    return {
      month: p.month,
      label: monthLabel(p.month),
      planAmount: p.amount,
      actualAmount: raw,
      planValue: toNum(p.amount),
      // `null` → a Recharts megszakítja a vonalat (nem 0-ra húzza).
      actualValue: raw === null ? null : toNum(raw),
    };
  });

  return (
    <div>
      <div className="mb-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
        <span className="text-muted-foreground flex items-center gap-1.5">
          <span className="bg-muted-foreground/60 size-2 shrink-0 rounded-full" />
          {planLabel}
        </span>
        <span className="text-muted-foreground flex items-center gap-1.5">
          <span className="bg-brand size-2 shrink-0 rounded-full" />
          {actualLabel}
        </span>
      </div>
      <div className="text-muted-foreground h-44 w-full" aria-hidden="true">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={rows}
            margin={{ top: 8, right: 4, bottom: 0, left: 4 }}
            accessibilityLayer
          >
            <defs>
              <linearGradient id="rf-actual-area" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" className="[stop-color:rgb(var(--brand))] [stop-opacity:0.35]" />
                <stop offset="100%" className="[stop-color:rgb(var(--brand))] [stop-opacity:0]" />
              </linearGradient>
            </defs>
            <CartesianGrid vertical={false} stroke={AXIS_LINE} strokeDasharray="3 3" />
            <XAxis
              dataKey="label"
              tick={TICK_STYLE}
              tickLine={false}
              axisLine={{ stroke: AXIS_LINE }}
              interval="preserveStartEnd"
              minTickGap={16}
            />
            <YAxis
              tick={<MoneyTick format={fmtShort} />}
              tickLine={false}
              axisLine={false}
              width={72}
            />
            <Tooltip
              cursor={{ stroke: AXIS_LINE }}
              content={({ active, payload }) => {
                const row = payload?.[0]?.payload as (typeof rows)[number] | undefined;
                if (!active || !row) return null;
                return (
                  <TooltipCard
                    label={row.label}
                    rows={[
                      {
                        key: 'plan',
                        label: planLabel,
                        value: fmt(row.planAmount),
                        swatch: 'bg-muted-foreground/60',
                      },
                      // A nyitott hónapot KIHAGYJUK a tooltipből — nem 0-t írunk oda.
                      ...(row.actualAmount === null
                        ? []
                        : [
                            {
                              key: 'actual',
                              label: actualLabel,
                              value: fmt(row.actualAmount),
                              swatch: 'bg-brand',
                            },
                          ]),
                    ]}
                  />
                );
              }}
            />
            {/* A TERV a háttér-vonal (szaggatott, semleges): viszonyítás, nem a főszereplő. */}
            <Area
              type="monotone"
              dataKey="planValue"
              className="stroke-muted-foreground/60"
              strokeWidth={2}
              strokeDasharray="4 3"
              fill="none"
              dot={false}
              activeDot={false}
            />
            <Area
              type="monotone"
              dataKey="actualValue"
              className="stroke-brand"
              strokeWidth={2}
              fill="url(#rf-actual-area)"
              dot={false}
              activeDot={{ r: 4, className: 'fill-brand stroke-background', strokeWidth: 2 }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      <SrOnlyData
        ariaLabel={ariaLabel}
        items={rows.map((r) => ({
          key: r.month,
          label: r.label,
          value:
            r.actualAmount === null
              ? `${planLabel} ${fmt(r.planAmount)}`
              : `${planLabel} ${fmt(r.planAmount)}, ${actualLabel} ${fmt(r.actualAmount)}`,
        }))}
      />
    </div>
  );
}

/**
 * A vízszintes sáv-chartok magassága: SORONKÉNT fix hely, a kártyán belüli görgetéssel. Így MINDEN sor
 * megmarad (nincs „top 10 + egyéb" csonkolás — a 99 szolgáltatás mind kigörgethető), a kártya viszont nem nő
 * a végtelenbe. A `min` a kevés-soros esetre kell (egy 1-soros chart ne legyen 24px magas).
 */
const ROW_H = 30;
/** A vízszintes sáv JOBB végének lekerekítése (a halom utolsó, nem nulla szeletén). */
const END_RADIUS_H: [number, number, number, number] = [0, 4, 4, 0];
const barHeight = (rows: number): number => Math.max(96, rows * ROW_H);
/**
 * Hány sor LÁTSZIK görgetés nélkül. A görgető-doboz magassága SORRA PONTOS (`VISIBLE_ROWS * ROW_H`) —
 * enélkül (a korábbi `max-h-72` = 288 px, azaz 9,6 sor) a kártya alján MINDIG egy FÉLBEVÁGOTT sáv állt, és
 * a kártya úgy nézett ki, mintha az adat csonka lenne, nem pedig görgethető (user-lelet, mérve 1920 px-en).
 */
const VISIBLE_ROWS = 9;

// ── 2. Bevétel szolgáltatásonként — vízszintes sáv-chart ─────────────────────────────────────────
export function BarList({
  data,
  fmt,
  ariaLabel,
}: {
  data: { name: string; amount: string }[];
  fmt: Formatter;
  ariaLabel: string;
}): JSX.Element {
  const rows = data.map((d) => ({ name: d.name, amount: d.amount, v: toNum(d.amount) }));

  return (
    <div>
      <div className="overflow-y-auto pr-1" style={{ maxHeight: VISIBLE_ROWS * ROW_H }}>
        <div
          className="text-muted-foreground w-full"
          style={{ height: barHeight(rows.length) }}
          aria-hidden="true"
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={rows}
              layout="vertical"
              margin={{ top: 0, right: 8, bottom: 0, left: 0 }}
              barCategoryGap={6}
              accessibilityLayer
            >
              <CartesianGrid horizontal={false} stroke={AXIS_LINE} strokeDasharray="3 3" />
              <XAxis type="number" hide />
              {/* A név a tengelyen: fix szélesség + a Recharts vágja — a teljes név a tooltipben van. */}
              <YAxis
                type="category"
                dataKey="name"
                tick={<CategoryTick />}
                tickLine={false}
                axisLine={false}
                width={CATEGORY_AXIS_WIDTH}
              />
              <Tooltip
                cursor={{ className: 'fill-muted/40' }}
                content={({ active, payload }) => {
                  const row = payload?.[0]?.payload as (typeof rows)[number] | undefined;
                  if (!active || !row) return null;
                  return (
                    <TooltipCard label={row.name} rows={[{ key: 'a', value: fmt(row.amount) }]} />
                  );
                }}
              />
              <Bar dataKey="v" className="fill-brand" radius={[0, 4, 4, 0]} maxBarSize={14} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      <SrOnlyData
        ariaLabel={ariaLabel}
        items={rows.map((r) => ({ key: r.name, label: r.name, value: fmt(r.amount) }))}
      />
    </div>
  );
}

// ── 3. Top 10 ügyfél — halmozott vízszintes sáv (ismétlődő + eseti) ─────────────────────────────
export function StackedBarList({
  data,
  fmt,
  recurringLabel,
  oneTimeLabel,
  ariaLabel,
  onSelect,
}: {
  /** A KÉT szelet (a címkéjük a hívótól jön): ügyfeleknél ismétlődő / eseti, szolgáltatásoknál fix / használat-alapú. */
  data: { name: string; recurring: string; oneTime: string; total: string }[];
  fmt: Formatter;
  recurringLabel: string;
  oneTimeLabel: string;
  ariaLabel: string;
  /**
   * KATTINTÁSRA SZŰRÉS (opcionális): a sor nevét kapja. A kattintás a diagram SZINTJÉN fog (`activeLabel`), így a
   * sor teljes szélességében — címkén és sávon — működik. Billentyűzetről ugyanez a szűrő-sávon érhető el.
   */
  onSelect?: ((name: string) => void) | undefined;
}): JSX.Element {
  const rows = data.map((d) => ({
    name: d.name,
    recurring: d.recurring,
    oneTime: d.oneTime,
    total: d.total,
    rec: toNum(d.recurring),
    one: toNum(d.oneTime),
  }));

  return (
    <div className="space-y-3">
      {/* Jelmagyarázat — a sáv-színekkel egyező pöttyök. */}
      <div className="text-muted-foreground flex items-center justify-end gap-3 text-xs">
        <span className="flex items-center gap-1.5">
          <span className="bg-brand size-2.5 rounded-full" aria-hidden="true" />
          {recurringLabel}
        </span>
        <span className="flex items-center gap-1.5">
          <span className="bg-warning-400 size-2.5 rounded-full" aria-hidden="true" />
          {oneTimeLabel}
        </span>
      </div>
      <div className="overflow-y-auto pr-1" style={{ maxHeight: VISIBLE_ROWS * ROW_H }}>
        <div
          className="text-muted-foreground w-full"
          style={{ height: barHeight(rows.length) }}
          aria-hidden="true"
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={rows}
              layout="vertical"
              margin={{ top: 0, right: 8, bottom: 0, left: 0 }}
              barCategoryGap={6}
              accessibilityLayer
              {...(onSelect
                ? {
                    className: 'cursor-pointer',
                    onClick: (state: unknown) => {
                      // a sor az INDEXBŐL oldódik fel: függőleges elrendezésnél az `activeLabel` nem mindig jön
                      const { activeIndex, activeLabel } =
                        (state as { activeIndex?: unknown; activeLabel?: unknown } | null) ?? {};
                      const name = rows[Number(activeIndex)]?.name ?? activeLabel;
                      if (typeof name === 'string') onSelect(name);
                    },
                  }
                : {})}
            >
              <CartesianGrid horizontal={false} stroke={AXIS_LINE} strokeDasharray="3 3" />
              <XAxis type="number" hide />
              <YAxis
                type="category"
                dataKey="name"
                tick={<CategoryTick />}
                tickLine={false}
                axisLine={false}
                width={CATEGORY_AXIS_WIDTH}
              />
              <Tooltip
                cursor={{ className: 'fill-muted/40' }}
                content={({ active, payload }) => {
                  const row = payload?.[0]?.payload as (typeof rows)[number] | undefined;
                  if (!active || !row) return null;
                  return (
                    <TooltipCard
                      label={row.name}
                      rows={[
                        {
                          key: 'rec',
                          label: recurringLabel,
                          value: fmt(row.recurring),
                          swatch: 'bg-brand',
                        },
                        {
                          key: 'one',
                          label: oneTimeLabel,
                          value: fmt(row.oneTime),
                          swatch: 'bg-warning-400',
                        },
                      ]}
                    />
                  );
                }}
              />
              {/* Halmozott: EGY `stackId` — az összeg a `total`-lal egyezik (a BFF számolja, itt nem összegzünk).
                  A KEREK VÉG mindig a sáv UTOLSÓ, nem nulla szeletén áll: ha a második szelet 0 (csak ismétlődő /
                  csak fix díj), az első kapja — különben a sáv vége szögletes maradna (user-lelet). */}
              <Bar
                dataKey="rec"
                stackId="a"
                className="fill-brand"
                maxBarSize={14}
                shape={(props: BarShapeProps) => (
                  <Rectangle
                    {...props}
                    radius={(rows[props.index]?.one ?? 0) > 0 ? 0 : END_RADIUS_H}
                  />
                )}
              />
              <Bar
                dataKey="one"
                stackId="a"
                className="fill-warning-400"
                radius={END_RADIUS_H}
                maxBarSize={14}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      <SrOnlyData
        ariaLabel={ariaLabel}
        items={rows.map((r) => ({
          key: r.name,
          label: r.name,
          value: `${fmt(r.total)} (${recurringLabel}: ${fmt(r.recurring)}, ${oneTimeLabel}: ${fmt(r.oneTime)})`,
        }))}
      />
    </div>
  );
}

// ── 4. Főkategória-megoszlás — donut ────────────────────────────────────────────────────────────
export function DonutChart({
  data,
  fmt,
  fmtShort,
  categoryLabel,
  ariaLabel,
  colorOf,
}: {
  data: { category: string; amount: string }[];
  fmt: Formatter;
  fmtShort: Formatter;
  categoryLabel: (category: string) => string;
  ariaLabel: string;
  /**
   * SZÍN-FELÜLÍRÁS (opcionális): a kulcs → `fill-*` / `bg-*` osztálypár. Alapból a bevétel FŐKATEGÓRIÁINAK rögzített
   * színe (`product` / `advisory` / `other`); a kiadás-kategóriák szabad kulcsai a hívó palettáját kapják.
   */
  colorOf?: ((category: string) => { fill: string; swatch: string }) | undefined;
}): JSX.Element {
  const fillOf = (c: string): string =>
    colorOf ? colorOf(c).fill : (CATEGORY_FILL[c] ?? 'fill-neutral-400');
  const swatchOf = (c: string): string =>
    colorOf ? colorOf(c).swatch : (CATEGORY_SWATCH[c] ?? 'bg-neutral-400');
  const rows = data.map((d) => ({
    category: d.category,
    label: categoryLabel(d.category),
    amount: d.amount,
    v: toNum(d.amount),
  }));
  const total = rows.reduce((a, r) => a + r.v, 0);
  const { i18n } = useTranslation();
  // EGÉSZ százalék, nyelvfüggő formázással (a tizedesjel itt nem jelenik meg, de az egységes út ez)
  const pct = (v: number): string =>
    formatPercent(total > 0 ? Math.round((v / total) * 100) : 0, i18n.language, {
      fractionDigits: 0,
    });

  return (
    // `h-full` + függőleges közép: a donut-kártya a RÁCS többi kártyájának magasságára nyúlik (a sáv-chartok
    // magasabbak), és a gyűrű a doboz TETEJÉHEZ tapadt — alatta egy fél kártyányi üres területtel.
    // TÖRŐ sor (`flex-wrap`), nem képernyő-töréspont: a fánk SZŰK kártyában is állhat (1280 px-en a háromhasábos
    // rács 320 px-es kártyája) — ott a jelmagyarázat a gyűrű ALÁ törik, ahelyett hogy a gyűrű balra kilógna, a
    // nevek pedig „P…"-re csonkulnának (mérve). Széles kártyában a kettő egymás mellett marad.
    <div className="flex h-full flex-wrap items-center justify-center gap-4 sm:gap-6">
      <div className="relative size-40 shrink-0" aria-hidden="true">
        {/* A közép-összeg a grafikon ELŐTT áll a DOM-ban, tehát ALATTA rajzolódik: a tooltip így eltakarja, nem
            fölé íródik (user-lelet — utána állva a felirat a tooltip szövegébe olvadt). */}
        {/* Közép: rövidített ÖSSZEG (a donut lyukában — a Recharts label-je itt kevésbé pontosan pozicionál). */}
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <span className="text-foreground text-sm font-semibold tabular-nums">
            {fmtShort(String(total))}
          </span>
        </div>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={rows}
              dataKey="v"
              nameKey="label"
              cx="50%"
              cy="50%"
              innerRadius="62%"
              outerRadius="100%"
              paddingAngle={total > 0 ? 2 : 0}
              stroke="none"
              // A 12 órától indul és óramutató-irányban halad (a korábbi -rotate-90 SVG-vel egyező olvasat).
              startAngle={90}
              endAngle={-270}
            >
              {rows.map((r) => (
                <Cell key={r.category} className={fillOf(r.category)} />
              ))}
            </Pie>
            <Tooltip
              content={({ active, payload }) => {
                const row = payload?.[0]?.payload as (typeof rows)[number] | undefined;
                if (!active || !row) return null;
                return (
                  <TooltipCard
                    label={row.label}
                    rows={[{ key: 'a', value: `${fmt(row.amount)} (${pct(row.v)})` }]}
                  />
                );
              }}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
      {/* Jelmagyarázat + % + pontos összeg — EZ a hozzáférhető adat-forma (nem `sr-only`, mert látszik is). */}
      <ul className="min-w-48 max-w-xs flex-1 space-y-1.5" aria-label={ariaLabel}>
        {rows.map((r) => (
          <li key={r.category} className="flex items-center gap-2 text-sm">
            <span
              className={`${swatchOf(r.category)} size-2.5 shrink-0 rounded-full`}
              aria-hidden="true"
            />
            <span className="text-foreground min-w-0 flex-1 truncate">{r.label}</span>
            <span className="text-muted-foreground shrink-0 tabular-nums">{pct(r.v)}</span>
            <span className="text-foreground shrink-0 tabular-nums">{fmt(r.amount)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
